<template>
  <v-row>
    <v-col cols="12">
      <div class="upload-section">
        <v-progress-circular
          v-if="showPreloader"
          indeterminate
          color="primary"
          size="32"
          class="ml-4"></v-progress-circular>
        <input
          ref="pdfInput"
          :loading="showPreloader"
          type="file"
          accept=".tif, .tiff, .pdf, .png, .jpg, .jpeg"
          :disabled="props.disabled"
          :class="{ 'hidden-input': showPreloader }"
          @change="handleFileUpload" />
        <v-alert v-if="uploadError" type="error" variant="tonal" density="compact" class="mt-2">
          {{ uploadError }}
        </v-alert>
      </div>
    </v-col>
  </v-row>
</template>

<script setup>
import { ref, watch } from "vue";
import { uuid } from "vue-uuid";
import { uploadService, validateFile, parseUploadError } from "@/services/uploadService";

const props = defineProps({
  downloadDestination: {
    type: String,
    default() {
      return "";
    },
  },
  downloadLabel: {
    type: String,
    default() {
      return "";
    },
  },
  downloadDestinationIndex: {
    type: Number,
    default() {
      return null;
    },
  },
  disabled: {
    type: Boolean,
    default() {
      return false;
    },
  },
});

const emit = defineEmits(["downloadLink"]);
const showPreloader = ref(false);
const downloadLink = ref(null);
const pdfInput = ref(null);
const uploadError = ref(null);

let lastUploadKey = null;
let lastFileName = null;

const handleFileUpload = async (event) => {
  showPreloader.value = true;
  uploadError.value = null;
  const file = event.target.files[0];
  if (!file) {
    showPreloader.value = false;
    return;
  }
  const validationError = validateFile(file);
  if (validationError) {
    uploadError.value = validationError;
    showPreloader.value = false;
    return;
  }
  const uploadKey = uuid.v4() + "/" + file.name;
  try {
    downloadLink.value = await uploadService.uploadFile(file, uploadKey);
    lastFileName = file.name;
    lastUploadKey = uploadKey;
  } catch (error) {
    uploadError.value = parseUploadError(error);
    if (pdfInput.value) pdfInput.value.value = "";
  } finally {
    showPreloader.value = false;
  }
};

watch(downloadLink, (newVal) => {
  if (newVal) {
    emit("downloadLink", {
      link: newVal,
      storeVar: props.downloadDestination,
      label: props.downloadLabel,
      storeVarIndex: props.downloadDestinationIndex,
      fileName: lastFileName,
      uploadKey: lastUploadKey,
    });
  }
});
</script>

<style lang="scss" scoped>
@import "../styles/components/_upload-file-component.scss";

.upload-loader {
  position: absolute;
  z-index: 1;
}

.hidden-input {
  opacity: 0;
  position: absolute;
  z-index: 0;
}
</style>
